import React, { useState, useCallback, useEffect } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  View,
  StyleSheet,
} from "react-native";
import { getCurrentLocation } from "@utils/getCurrentLocation";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import {
  getAvailableSpokes,
  requestSpokeChange,
} from "@services/spokeChangeApi";
import type {
  DeliveryHub,
  SpokeChangeType as SpokeChangeTypeValue,
} from "@screens/Onboarding/types";
import { requestLocation } from "@utils/permissions";
import { fontFamily } from "@utils/styles/fonts";

const textPrimary = color.Gray20;
const textMuted = color.color_808080;
const borderDefault = color.color_D9D9D9;
const dividerColor = color.lineColor;
const shadowColor = color.black;

type SpokeChangeStackParamList = {
  SpokeChangeListing: { changeType: SpokeChangeTypeValue } | undefined;
  SpokeChangeSubmitted: undefined;
};

// Maps backend CustomError.message → human-readable toast text.
const ERROR_MESSAGES: Record<string, string> = {
  NEW_CP_ID_REQUIRED: "Please select a spoke to continue.",
  INVALID_CHANGE_TYPE: "Invalid change type. Please try again.",
  ACTIVE_CP_DB_NOT_FOUND: "Your account is not active. Contact support.",
  SAME_SPOKE: "You are already assigned to this spoke.",
  ON_TEMPORARY_ASSIGNMENT:
    "You are already on a temporary assignment. Wait until it reverts.",
  SPOKE_NOT_FOUND: "Selected spoke is no longer available.",
  PENDING_REQUEST_EXISTS:
    "You already have a pending spoke change request. Cancel it first.",
  SPOKE_SWITCH_BLOCKED:
    "Spoke switch is not allowed right now — complete pending deliveries and submit recon first.",
};

const SpokeChangeListing: React.FC = () => {
  const navigation =
    useNavigation<
      StackNavigationProp<SpokeChangeStackParamList, "SpokeChangeListing">
    >();
  const route =
    useRoute<RouteProp<SpokeChangeStackParamList, "SpokeChangeListing">>();

  // SpokeChangeType passes this through. Falls back to TEMPORARY when the
  // screen is entered directly (deep link) so the request is never sent typeless.
  const changeType: SpokeChangeTypeValue =
    route.params?.changeType ?? "TEMPORARY";
  const isTemporary = changeType === "TEMPORARY";

  const [spokes, setSpokes] = useState<DeliveryHub[]>([]);
  const [selectedSpoke, setSelectedSpoke] = useState<DeliveryHub | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadSpokes = async (geo?: { lat: number; lng: number }) => {
      try {
        const result = await getAvailableSpokes(geo);
        if (!cancelled) setSpokes(result.spokes);
      } catch (err) {
        if (cancelled) return;
        const axiosErr = err as { response?: { data?: { message?: string } } };
        toast.showLongToast(
          ERROR_MESSAGES[axiosErr?.response?.data?.message ?? ""] ||
            axiosErr?.response?.data?.message ||
            "Failed to load spokes. Try again.",
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    // Try to fetch with current location so backend ranks by distance.
    // Fall back to a no-geo fetch if permission is denied or GPS times out.
    (async () => {
      const { granted } = await requestLocation();
      if (cancelled) return;
      if (!granted) {
        loadSpokes();
        return;
      }
      try {
        const { coords } = await getCurrentLocation({ timeoutMs: 10000 });
        if (cancelled) return;
        loadSpokes({ lat: coords.latitude, lng: coords.longitude });
      } catch {
        if (cancelled) return;
        loadSpokes();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = useCallback(async () => {
    if (!selectedSpoke || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await requestSpokeChange(selectedSpoke.id, changeType);
      navigation.navigate("SpokeChangeSubmitted");
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      const code = axiosError?.response?.data?.message ?? "";
      toast.showLongToast(
        ERROR_MESSAGES[code] ||
          code ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSpoke, isSubmitting, changeType, navigation]);

  const renderSpokeCard = useCallback(
    ({ item }: { item: DeliveryHub }) => {
      const isSelected = selectedSpoke?.id === item.id;

      return (
        <Pressable
          style={[styles.hubCard, isSelected && styles.hubCardSelected]}
          onPress={() => setSelectedSpoke(item)}
          accessibilityRole="radio"
          accessibilityState={{ selected: isSelected }}
          accessibilityLabel={item.name}
        >
          <View style={styles.hubCardHeader}>
            <Text style={styles.hubName}>{item.name}</Text>
            {isSelected && <Text style={styles.hubCardCheck}>✓</Text>}
          </View>
          <Text variant="caption_2" style={styles.hubAddress}>
            {item.address}
          </Text>
          {item.hasIncentive && item.incentiveAmount ? (
            <View style={styles.incentiveBadge}>
              <Text variant="caption_1" style={styles.incentiveText}>
                {item.incentiveAmount}
              </Text>
            </View>
          ) : null}
        </Pressable>
      );
    },
    [selectedSpoke],
  );

  return (
    <>
      <View style={styles.container}>
        <HeaderWithBackAndTitle
          headertitle={
            isTemporary
              ? "Change Spoke (Temporary)"
              : "Change Spoke (Permanent)"
          }
          navigation={navigation}
          hideShadow
        />

        <Text variant="caption_2" style={styles.helperText}>
          {isTemporary
            ? "Select a nearby hub to temporarily switch. You will auto-revert to your home spoke the next day."
            : "Select the hub you want to move to permanently. The new hub's executive will review your request."}
        </Text>

        {isLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator color={color.primary} />
          </View>
        ) : spokes.length === 0 ? (
          <Text style={styles.emptyText}>
            No other spokes are available right now.
          </Text>
        ) : (
          <FlatList
            data={spokes}
            renderItem={renderSpokeCard}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          />
        )}

        <View style={styles.bottomBar}>
          <Pressable
            style={[
              styles.submitButton,
              !selectedSpoke && styles.submitButtonDisabled,
            ]}
            disabled={!selectedSpoke || isSubmitting}
            onPress={onSubmit}
            accessibilityRole="button"
            accessibilityLabel="Submit Spoke Change Request"
          >
            {isSubmitting ? (
              <ActivityIndicator color={color.white} />
            ) : (
              <Text variant="title_16" style={styles.submitButtonText}>
                Submit Request
              </Text>
            )}
          </Pressable>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: color.white },
  helperText: {
    color: textMuted,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
    lineHeight: 18,
  },
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: textMuted,
    textAlign: "center",
    marginTop: 32,
    paddingHorizontal: 24,
  },
  listContent: { paddingHorizontal: 20, paddingTop: 8, paddingBottom: 100 },
  hubCard: {
    borderWidth: 1,
    borderColor: borderDefault,
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    backgroundColor: color.white,
  },
  hubCardSelected: {
    borderColor: color.primary,
    borderWidth: 1.5,
    backgroundColor: color.veryLightOrange_rgb,
  },
  hubCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  hubCardCheck: {
    fontSize: 16,
    color: color.primary,
  },
  hubName: {
    fontSize: 15,
    fontFamily: fontFamily.bold,
    color: textPrimary,
    flex: 1,
    marginRight: 8,
  },
  hubAddress: { color: textMuted, marginBottom: 10, lineHeight: 18 },
  incentiveBadge: {
    backgroundColor: color.veryLightGreen,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: "flex-start",
  },
  incentiveText: { color: color.plain_green },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
  submitButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  submitButtonDisabled: { backgroundColor: color.lighterBlack },
  submitButtonText: { color: color.white },
});

export default SpokeChangeListing;
